import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Camera, Plus, X } from "lucide-react";
import styles from "./OrderImageCapture.module.scss";

const formatCaptureTime = (date) => {
  const time = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `${time} • ${day}-${month}-${year}`;
};

const OrderImageCapture = ({
  images,
  isUploading,
  uploadError,
  onAddFiles,
  onRemoveImage,
  onClose,
}) => {
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const reviewPreviewRef = useRef("");
  const [cameraError, setCameraError] = useState("");
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [isReviewing, setIsReviewing] = useState(images.length > 0);
  const [reviewPreviewUrl, setReviewPreviewUrl] = useState(
    images[images.length - 1]?.previewUrl || ""
  );
  const capturedAtRef = useRef(new Date());
  const capturedBy =
    window.localStorage.getItem("rk_user_name") ||
    window.localStorage.getItem("rk_user_phone") ||
    "User";

  useEffect(() => {
    let stream;
    let cancelled = false;

    const startCamera = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError("Camera preview is not supported on this device");
        return;
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: "environment" } },
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch {
        if (!cancelled) {
          setCameraError("Allow camera access, or choose an image instead");
        }
      }
    };

    startCamera();
    return () => {
      cancelled = true;
      stream?.getTracks().forEach((track) => track.stop());
      if (reviewPreviewRef.current) {
        URL.revokeObjectURL(reviewPreviewRef.current);
      }
    };
  }, []);

  const showReviewPreview = (url, { revoke = false } = {}) => {
    if (reviewPreviewRef.current) {
      URL.revokeObjectURL(reviewPreviewRef.current);
    }
    reviewPreviewRef.current = revoke ? url : "";
    setReviewPreviewUrl(url);
    setIsReviewing(true);
    capturedAtRef.current = new Date();
  };

  const captureImage = async () => {
    const video = videoRef.current;
    if (!video?.videoWidth || isUploading) return;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) return;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.9)
    );
    if (!blob) return;

    const previewUrl = URL.createObjectURL(blob);
    showReviewPreview(previewUrl, { revoke: true });
    const file = new File([blob], `return-${Date.now()}.jpg`, {
      type: "image/jpeg",
    });
    await onAddFiles([file]);
  };

  const handleFileSelection = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length === 0) return;

    const previewUrl = URL.createObjectURL(files[files.length - 1]);
    showReviewPreview(previewUrl, { revoke: true });
    await onAddFiles(files);
  };

  const latestUploadedPreview = images[images.length - 1]?.previewUrl;
  const visiblePreview = reviewPreviewUrl || latestUploadedPreview;

  return (
    <div
      className={styles.captureOverlay}
      role="dialog"
      aria-modal="true"
      aria-label="Capture return images"
      aria-busy={isUploading}
    >
      <header className={styles.captureHeader}>
        <button
          type="button"
          className={styles.backButton}
          onClick={onClose}
          disabled={isUploading}
          aria-label="Back to order"
        >
          <ArrowLeft size={22} />
        </button>
        <h1>Capture image</h1>
      </header>

      <main className={styles.captureContent}>
        <div className={styles.cameraStage}>
          <video
            ref={videoRef}
            className={`${styles.cameraPreview} ${
              isReviewing ? styles.cameraPreviewHidden : ""
            }`}
            autoPlay
            muted
            playsInline
            onCanPlay={() => setIsCameraReady(true)}
          />

          {isReviewing && visiblePreview ? (
            <img
              className={styles.cameraPreview}
              src={visiblePreview}
              alt="Captured return"
            />
          ) : null}

          {!isReviewing && cameraError ? (
            <div className={styles.cameraFallback}>
              <Camera size={38} />
              <p>{cameraError}</p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose image
              </button>
            </div>
          ) : null}

          {isReviewing && visiblePreview ? (
            <div className={styles.captureMetadata}>
              <strong>{formatCaptureTime(capturedAtRef.current)}</strong>
              <span>Captured by: {capturedBy}</span>
            </div>
          ) : null}
        </div>

        {!isReviewing ? (
          <div className={styles.shutterBar}>
            <button
              type="button"
              className={styles.shutterButton}
              onClick={captureImage}
              disabled={!isCameraReady || isUploading}
              aria-label="Capture image"
            >
              <span />
            </button>
          </div>
        ) : (
          <div className={styles.reviewPanel}>
            <div className={styles.thumbnailRow}>
              <button
                type="button"
                className={styles.addImageButton}
                onClick={() => {
                  if (cameraError) {
                    fileInputRef.current?.click();
                    return;
                  }
                  setIsReviewing(false);
                }}
                disabled={isUploading}
                aria-label="Add another image"
              >
                <Plus size={26} />
              </button>

              {images.map((image) => (
                <div key={image.id} className={styles.thumbnail}>
                  <img src={image.previewUrl} alt="Return evidence" />
                  <button
                    type="button"
                    onClick={() => onRemoveImage(image.id)}
                    disabled={isUploading}
                    aria-label="Remove image"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}

              {isUploading ? (
                <span className={styles.uploadingText}>Uploading…</span>
              ) : null}
            </div>

            {uploadError ? (
              <div className={styles.uploadError} role="alert">
                {uploadError}
              </div>
            ) : null}

            <button
              type="button"
              className={styles.doneButton}
              onClick={onClose}
              disabled={isUploading}
            >
              Done
            </button>
          </div>
        )}
      </main>

      <input
        ref={fileInputRef}
        className={styles.hiddenInput}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        onChange={handleFileSelection}
      />
    </div>
  );
};

export default OrderImageCapture;
